/**
 * What was decided for you.
 *
 * This screen replaces a rule rather than adding a feature. Every comparison
 * used to wait behind a representative's sign-off, which read as care and
 * worked as an off switch: three hundred and sixty of them, two people who
 * teach full time, and a protect board that showed nothing until somebody had
 * been through the lot.
 *
 * So the judgements stand on their own now, and this is where a person finds
 * out what they say. The shape follows from that. It is a report, so the
 * expected action is to read it and close it. Nothing is a decision waiting to
 * be made — there is no approve button, because approving what already applies
 * is theatre, and a screen full of buttons that change nothing teaches people
 * to click without reading.
 *
 * Corrections come first, and both readings are always on the row. Winding
 * something back is a choice between two verdicts, so showing only the
 * surviving one would be asking for agreement rather than a decision. The
 * pipeline said one thing, the adjudication says another, and the person
 * reading gets to see the disagreement it is asking them to arbitrate.
 *
 * The list is ordered by how much it changed, not by clause number or time.
 * Twenty-two rows saying "the pipeline was right" are a number in the summary,
 * not twenty-two cards to scroll past on the way to the fourteen that matter.
 */

import { useState, type CSSProperties } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type Adjudication, type AdjudicationReport } from '../lib/api.js';

const VERDICT_LABEL: Record<string, string> = {
  primary_better: 'we are ahead',
  comparable: 'about the same',
  comparator_better: 'they are ahead',
  no_primary_equivalent: 'we have nothing on it',
  no_comparator_equivalent: 'no floor underneath',
};

const DISPOSITION: Record<string, { label: string; hue: string; blurb: string }> = {
  corrected: {
    label: 'Changed',
    hue: 'var(--color-behind)',
    blurb: 'The comparison held. The verdict on it did not.',
  },
  withdrawn: {
    label: 'Withdrawn',
    hue: 'var(--color-defect)',
    blurb: 'The two clauses are not about the same thing, so nothing is published from this pair.',
  },
  stands: {
    label: 'Confirmed',
    hue: 'var(--color-muted)',
    blurb: 'Read and left as it was.',
  },
};

function verdict(key: string): string {
  return VERDICT_LABEL[key] ?? key;
}

function Card({ item }: { item: Adjudication }) {
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const [open, setOpen] = useState(false);
  const kind = DISPOSITION[item.disposition] ?? DISPOSITION['stands']!;

  const invalidate = (): void => {
    /*
     * The protect board and the plain surface both read the settled verdict,
     * so winding one back changes them. Without this the report updates and
     * the two screens the change was made for keep showing the old reading
     * until a reload — which reads as the button having done nothing.
     */
    void queryClient.invalidateQueries({ queryKey: ['adjudications'] });
    void queryClient.invalidateQueries({ queryKey: ['protect'] });
    void queryClient.invalidateQueries({ queryKey: ['plain'] });
  };

  const wind = useMutation({
    mutationFn: () => api.windBack(item.id, note.trim()),
    onSuccess: () => {
      setOpen(false);
      setNote('');
      invalidate();
    },
  });

  const restore = useMutation({
    mutationFn: () => api.restoreAdjudication(item.id),
    onSuccess: invalidate,
  });

  const changed = item.settledVerdict !== item.pipelineVerdict;

  return (
    <li className="section-rule py-5">
      <div className="row mb-1">
        <span className="pill" style={{ '--pill-hue': kind.hue } as CSSProperties}>
          {kind.label}
        </span>
        <a className="text-[15px] font-medium" href={`#/read/c/${encodeURIComponent(item.primary.number)}`}>
          {item.primary.number}
          {item.primary.heading ? ` ${item.primary.heading}` : ''}
        </a>
        {item.compare && (
          <span className="text-[13px] text-[var(--color-muted)]">
            against {item.compare.instrument} {item.compare.number}
          </span>
        )}
      </div>

      {/*
        Both verdicts, side by side, whenever they differ. The arrow is the
        whole argument of the row: this is what changed, and you are being
        shown it rather than told a conclusion.
      */}
      {changed ? (
        <p className="mb-2 text-[15px]">
          <span className="text-[var(--color-muted)] line-through">{verdict(item.pipelineVerdict)}</span>
          <span className="mx-2 text-[var(--color-muted)]" aria-label="became">
            →
          </span>
          <span className="font-medium">
            {item.disposition === 'withdrawn' ? 'nothing published' : verdict(item.settledVerdict)}
          </span>
        </p>
      ) : (
        <p className="mb-2 text-[15px] text-[var(--color-muted)]">{verdict(item.settledVerdict)}</p>
      )}

      {item.settledNote && (
        <p className="measure-wide mb-2 text-[15px] leading-relaxed">{item.settledNote}</p>
      )}

      {item.suggestedNumber && (
        <p className="label mb-2 text-[11px] text-[var(--color-muted)]">
          Better matched against {item.suggestedNumber}
        </p>
      )}

      {item.pipelineNote && changed && (
        <details className="mb-2">
          <summary className="label cursor-pointer text-[11px] text-[var(--color-muted)]">
            What the first pass said
          </summary>
          <p className="measure-wide mt-1 text-[14px] leading-relaxed text-[var(--color-muted)]">
            {item.pipelineNote}
          </p>
        </details>
      )}

      {item.woundBack ? (
        <div className="callout measure-wide" style={{ '--callout-hue': 'var(--color-muted)' } as CSSProperties}>
          <span className="callout-glyph" aria-hidden>
            ⟲
          </span>
          <p className="text-[14px] leading-relaxed">
            Taken back{item.woundBack.by ? ` by ${item.woundBack.by}` : ''}
            {item.woundBack.note ? ` — ${item.woundBack.note}` : ''}.{' '}
            <button
              type="button"
              className="underline"
              onClick={() => restore.mutate()}
              disabled={restore.isPending}
            >
              Let it stand again
            </button>
          </p>
        </div>
      ) : open ? (
        <form
          className="row-flush gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (note.trim().length >= 3) wind.mutate();
          }}
        >
          <input
            className="flex-1 border-b border-[var(--color-rule)] bg-transparent py-1 text-[14px]"
            placeholder="Why should this not stand?"
            aria-label={`Why the judgement on clause ${item.primary.number} should not stand`}
            value={note}
            autoFocus
            onChange={(event) => setNote(event.target.value)}
          />
          <button type="submit" className="pill" disabled={note.trim().length < 3 || wind.isPending}>
            Take it back
          </button>
          <button type="button" className="pill" onClick={() => setOpen(false)}>
            Cancel
          </button>
        </form>
      ) : (
        <button
          type="button"
          className="text-[13px] text-[var(--color-muted)] underline"
          onClick={() => setOpen(true)}
        >
          Take this one back
        </button>
      )}
    </li>
  );
}

function WindBackAll({ standing }: { standing: number }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');

  const all = useMutation({
    mutationFn: () => api.windBackAll(note.trim()),
    onSuccess: () => {
      setOpen(false);
      setNote('');
      void queryClient.invalidateQueries({ queryKey: ['adjudications'] });
      void queryClient.invalidateQueries({ queryKey: ['protect'] });
      void queryClient.invalidateQueries({ queryKey: ['plain'] });
    },
  });

  if (standing === 0) return null;

  return (
    <div className="section-rule-below pb-4">
      {open ? (
        <form
          className="row-flush gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (note.trim().length >= 3) all.mutate();
          }}
        >
          <input
            className="flex-1 border-b border-[var(--color-rule)] bg-transparent py-1 text-[14px]"
            placeholder={`Why take all ${standing} back?`}
            aria-label="Why the whole set of judgements should be taken back"
            value={note}
            autoFocus
            onChange={(event) => setNote(event.target.value)}
          />
          <button type="submit" className="pill" disabled={note.trim().length < 3 || all.isPending}>
            Take all {standing} back
          </button>
          <button type="button" className="pill" onClick={() => setOpen(false)}>
            Cancel
          </button>
        </form>
      ) : (
        <button
          type="button"
          className="text-[13px] text-[var(--color-muted)] underline"
          onClick={() => setOpen(true)}
        >
          Take all {standing} back
        </button>
      )}
      {/*
        This exists so that losing confidence in the model is one action rather
        than several hundred. Without it, a representative who decided the
        adjudications were not trustworthy would have to undo them one at a
        time — which is the queue this whole change removed, approached from
        the other side.
      */}
    </div>
  );
}

export function Report() {
  const report = useQuery({ queryKey: ['adjudications'], queryFn: api.adjudications });
  const data: AdjudicationReport | undefined = report.data;

  if (!data) return <p className="screen-pad text-[15px] text-[var(--color-muted)]">Loading…</p>;

  const { totals } = data;
  const standing = data.adjudications.filter((a) => a.woundBack === null).length;
  const changed = data.adjudications.filter((a) => a.disposition !== 'stands').length;

  return (
    <main className="screen screen-pad">
      <div className="screen-column">
        <h1 className="mb-1 text-[21px] font-medium">What was decided for you</h1>
        <p className="measure-wide mb-6 text-[15px] leading-relaxed text-[var(--color-muted)]">
          These comparisons were read and settled without waiting for you. They are live on the
          protect board and in what the wider staff can see. Nothing here needs your approval —
          it is already applied. If one of them is wrong, take it back and the surfaces follow.
        </p>

        {/*
          Four numbers, and the largest of them is the one about work not yet
          done. A summary that counted only what has been read would say "36
          settled" and let a reader take that for the whole corpus.
        */}
        <dl className="row mb-6 gap-6 text-[15px]">
          <div>
            <dt className="label text-[11px] text-[var(--color-muted)]">Settled by reading</dt>
            <dd className="tabular-nums text-[19px]">{totals.model}</dd>
          </div>
          <div>
            <dt className="label text-[11px] text-[var(--color-muted)]">Signed off by a person</dt>
            <dd className="tabular-nums text-[19px]">{totals.person}</dd>
          </div>
          <div>
            <dt className="label text-[11px] text-[var(--color-muted)]">Withdrawn as mismatched</dt>
            <dd className="tabular-nums text-[19px]">{totals.withdrawn}</dd>
          </div>
          <div>
            <dt className="label text-[11px] text-[var(--color-muted)]">Not looked at yet</dt>
            <dd className="tabular-nums text-[19px] text-[var(--color-muted)]">{totals.unread}</dd>
          </div>
        </dl>

        <p className="measure-wide mb-4 text-[15px] leading-relaxed">
          {changed === 0
            ? 'Nothing has been changed from the first pass.'
            : `${changed} of ${data.adjudications.length} disagreed with the first pass and are listed first.`}
          {totals.woundBack > 0 && ` ${totals.woundBack} have been taken back.`}
        </p>

        <WindBackAll standing={standing} />

        <ul>
          {data.adjudications.map((item) => (
            <Card key={item.id} item={item} />
          ))}
        </ul>
      </div>
    </main>
  );
}
